import { decode } from 'he'
import { parseKeywordList } from 'lib/util/keywords'

export type ConfigureSearchOutcome = {
  result: 'match' | 'none' | 'error'
  recalls: any[]
  count: number
}

function topicToTaxonomyIds(topic: string): string[] | null {
  if (topic === 'food') return ['ca:cat:144', 'ca:issue:food']
  if (topic === 'allergies') return ['ca:issue:food/allergen']
  if (topic === 'drugs' || topic === 'health') return ['ca:cat:180']
  if (topic === 'children' || topic === 'consumer') return ['ca:cat:101']
  return null
}

function buildKeywordsSearchUrl(keywords: string, topic: string): string {
  const list = parseKeywordList(keywords)
  const terms =
    list.length > 0
      ? list.map(t => encodeURIComponent(t)).join(',')
      : encodeURIComponent(keywords.trim())
  const taxonomyIds = topicToTaxonomyIds(topic)
  const topicParam =
    taxonomyIds && taxonomyIds.length > 0
      ? `&topic=${encodeURIComponent(taxonomyIds.join(','))}`
      : ''
  return `/api/search?q=${terms}&limit=3${topicParam}`
}

function mapKeywordResult(raw: {
  apiId?: string
  title?: string
  date_published?: number
  category?: string[]
}): any | null {
  if (!raw.apiId || !raw.title) return null
  const apiId = raw.apiId
  // Recall formats the date; a missing one would throw and break step 2
  if (!Number.isFinite(raw.date_published)) return null
  return {
    apiId,
    recallId: apiId,
    category: raw.category && raw.category.length > 0 ? raw.category : ['4'],
    date_published: raw.date_published,
    start_date: raw.date_published,
    parsed: {
      title: decode(raw.title).trim(),
      basic_details: {},
    },
  }
}

export async function searchKeywordsConfigure(
  keywords: string,
  topic: string,
  signal: AbortSignal,
): Promise<ConfigureSearchOutcome> {
  const emptyError: ConfigureSearchOutcome = {
    result: 'error',
    recalls: [],
    count: 0,
  }
  try {
    const url = buildKeywordsSearchUrl(keywords, topic)
    const response = await fetch(url, { signal })
    if (!response.ok) return emptyError
    let json: { results?: unknown }
    try {
      json = await response.json()
    } catch {
      return emptyError
    }
    if (!Array.isArray(json.results)) return emptyError
    const recalls = json.results
      .map(mapKeywordResult)
      .filter((r): r is NonNullable<typeof r> => r != null)
      .slice(0, 3)
    if (recalls.length === 0) {
      return { result: 'none', recalls: [], count: 0 }
    }
    return { result: 'match', recalls, count: recalls.length }
  } catch {
    return emptyError
  }
}

export async function searchVehicleConfigure(
  firebase: any,
  make: string,
  model: string,
  year: string,
  signal: AbortSignal,
): Promise<ConfigureSearchOutcome> {
  const emptyError: ConfigureSearchOutcome = {
    result: 'error',
    recalls: [],
    count: 0,
  }
  const emptyNone: ConfigureSearchOutcome = {
    result: 'none',
    recalls: [],
    count: 0,
  }

  const run = async (): Promise<ConfigureSearchOutcome> => {
    let query = firebase
      .firestore()
      .collection('vehicles')
      .where('make', '==', make.trim()) as any
    if (model && model.trim()) {
      query = query.where('model', '==', model.trim())
    }
    if (year && String(year).trim()) {
      query = query.where('year', '==', String(year).trim())
    }
    const vehicleSnap = await query.limit(50).get()
    if (signal.aborted) return emptyError

    const apiIds = Array.from(
      new Set(
        vehicleSnap.docs
          .map((doc: { data: () => { apiId?: string } }) => doc.data().apiId)
          .filter(Boolean),
      ),
    ) as string[]

    if (apiIds.length === 0) return emptyNone

    const toFetch = apiIds.slice(0, 10)
    const recallDocs = await Promise.all(
      toFetch.map(async apiId => {
        const doc = await firebase.firestore().doc(`recalls/${apiId}`).get()
        return { apiId, doc }
      }),
    )
    if (signal.aborted) return emptyError

    const recalls = recallDocs
      .filter(({ doc }) => doc.exists)
      .map(({ apiId, doc }) => ({
        apiId,
        ...doc.data(),
      }))
      .sort((a, b) => (b.date_published ?? 0) - (a.date_published ?? 0))
      .slice(0, 3)

    if (recalls.length === 0) return emptyNone
    return { result: 'match', recalls, count: recalls.length }
  }

  try {
    const timeoutMs = 5000
    return await Promise.race([
      run(),
      new Promise<ConfigureSearchOutcome>((_, reject) => {
        const id = setTimeout(() => reject(new Error('timeout')), timeoutMs)
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(id)
            reject(new DOMException('Aborted', 'AbortError'))
          },
          { once: true },
        )
      }),
    ])
  } catch {
    return emptyError
  }
}
